import React, { useState } from 'react';
import { useSchool } from '../context/SchoolContext';
import { Search, ChevronDown, ChevronRight } from 'lucide-react';
import './Customers.css';

const Customers = () => {
    const { students, branches, loadMoreStudents, hasMoreStudents, totalStudentsCount, selectedBranch, setSelectedBranch } = useSchool();
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    // Normalize student data
    const mappedStudents = students.map(s => ({
        ...s,
        name: `${s.first_name || ''} ${s.last_name || ''}`.trim() || 'Unknown',
        displayId: s.student_id_number || s.customer_id || `#${s.id}`,
        packageName: s.package_name || s.package || 'N/A',
        paymentPlan: s.installment_type || 'Standard',
        stage: s.stage || 'New',
        balance: parseFloat(s.balance || 0),
        email: s.email_address || s.email || '—',
        phone: s.phone_number || s.phone || '—',
        branchName: s.branch_name || s.branch || '—',
        gender: s.gender || 'N/A',
        idNumber: s.national_id__no || 'N/A',
        address: s.address || 'N/A'
    }));

    const filteredStudents = mappedStudents.filter(s => {
        const matchesSearch =
            s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            s.displayId.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (s.email && s.email.toLowerCase().includes(searchTerm.toLowerCase()));
        const matchesBranch = selectedBranch === 'All Branches' ? true : (s.branch === selectedBranch || s.branchName === selectedBranch);
        return matchesSearch && matchesBranch;
    });

    const stageColors = {
        'New': 'stage-new',
        'Provisional': 'stage-provisional',
        'Simulator': 'stage-simulator',
        'Road Lesson': 'stage-road',
        'Graduated': 'stage-graduated',
    };

    return (
        <div className="page-container customers-page">
            <div className="page-header">
                <div>
                    <h2 className="page-title">Customers</h2>
                    <p className="page-subtitle">
                        {filteredStudents.length} student{filteredStudents.length !== 1 ? 's' : ''} enrolled
                    </p>
                </div>
            </div>

            {/* Filters */}
            <div className="filters-bar">
                <div className="search-input-wrapper">
                    <Search size={16} className="search-icon" />
                    <input
                        type="text"
                        placeholder="Search by name, ID or email..."
                        className="search-input"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="filter-select-wrapper">
                    <select
                        className="filter-select"
                        value={selectedBranch}
                        onChange={e => setSelectedBranch(e.target.value)}
                    >
                        <option value="All Branches">All Branches</option>
                        {branches.map(b => (
                            <option key={b} value={b}>
                                {b}
                            </option>
                        ))}
                    </select>
                    <ChevronDown size={14} className="filter-chevron" />
                </div>
            </div>

            {/* Table */}
            <div className="co-table-container">
                <table className="co-table">
                    <thead>
                        <tr>
                            <th>STUDENT</th>
                            <th>PACKAGE</th>
                            <th>STAGE</th>
                            <th>BRANCH</th>
                            <th>BALANCE</th>
                            <th>CONTACT</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredStudents.map(student => (
                            <tr
                                key={student.id}
                                className="co-row"
                                onClick={() => setSelectedCustomer(student)}
                            >
                                <td>
                                    <div className="co-student-cell">
                                        <div className="co-avatar">{student.name.charAt(0).toUpperCase()}</div>
                                        <div>
                                            <div className="co-name">{student.name}</div>
                                            <div className="co-id">{student.displayId}</div>
                                        </div>
                                    </div>
                                </td>
                                <td className="co-text-sm">{student.packageName}</td>
                                <td>
                                    <span className={`co-stage-badge ${stageColors[student.stage] || ''}`}>
                                        {student.stage}
                                    </span>
                                </td>
                                <td className="co-text-sm">{student.branchName}</td>
                                <td>
                                    <span className={student.balance > 0 ? 'co-balance-owed' : 'co-balance-clear'}>
                                        ${student.balance.toFixed(2)}
                                    </span>
                                </td>
                                <td className="co-text-sm co-contact">
                                    <div>{student.phone}</div>
                                    <div className="co-email">{student.email}</div>
                                </td>
                                <td>
                                    <ChevronRight size={16} className="co-chevron" />
                                </td>
                            </tr>
                        ))}
                        {filteredStudents.length === 0 && (
                            <tr>
                                <td colSpan={7} className="co-empty">
                                    No students found matching your search.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
                {hasMoreStudents && (
                    <div className="flex justify-center p-6 border-t border-slate-100">
                        <button className="btn btn-secondary" onClick={loadMoreStudents} style={{ padding: '0.5rem 2rem', borderRadius: '2rem' }}>
                            Load More ({students.length} of {totalStudentsCount})
                        </button>
                    </div>
                )}
            </div>

            {/* Detail Modal */}
            {selectedCustomer && (
                <div className="modal-overlay" onClick={() => setSelectedCustomer(null)}>
                    <div className="modal-content customer-detail-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <div className="co-modal-title-wrap">
                                <div className="co-avatar co-avatar-lg">{selectedCustomer.name.charAt(0)}</div>
                                <div>
                                    <h3>{selectedCustomer.name}</h3>
                                    <p className="co-id">{selectedCustomer.displayId}</p>
                                </div>
                            </div>
                            <button className="close-btn" onClick={() => setSelectedCustomer(null)}>✕</button>
                        </div>
                        <div className="modal-body">
                            <div className="detail-grid">
                                <div className="detail-section">
                                    <h4>Personal Information</h4>
                                    <p><strong>ID Number:</strong> {selectedCustomer.idNumber}</p>
                                    <p><strong>Gender:</strong> {selectedCustomer.gender}</p>
                                    <p><strong>Email:</strong> {selectedCustomer.email}</p>
                                    <p><strong>Phone:</strong> {selectedCustomer.phone}</p>
                                    <p><strong>Address:</strong> {selectedCustomer.address}</p>
                                </div>
                                <div className="detail-section">
                                    <h4>Programme Details</h4>
                                    <p><strong>Package:</strong> {selectedCustomer.packageName}</p>
                                    <p><strong>Stage:</strong> {selectedCustomer.stage}</p>
                                    <p><strong>Payment Plan:</strong> {selectedCustomer.paymentPlan}</p>
                                    <p><strong>Branch:</strong> {selectedCustomer.branchName}</p>
                                </div>
                                <div className="detail-section">
                                    <h4>Financial Status</h4>
                                    <p><strong>Balance:</strong>{' '}
                                        <span className={selectedCustomer.balance > 0 ? 'co-balance-owed' : 'co-balance-clear'}>
                                            ${selectedCustomer.balance.toFixed(2)}
                                        </span>
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-primary" onClick={() => setSelectedCustomer(null)}>Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Customers;
